import { CommonModule } from '@angular/common';
import { Component, Inject, input } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatCardModule } from '@angular/material/card';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ActivityInterface, CRMChangeLogApiService } from '@vendasta/crm';
import { GalaxyAvatarModule } from '@vendasta/galaxy/avatar';
import { GalaxyPipesModule } from '@vendasta/galaxy/pipes';

import { combineLatest, of, switchMap } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import {
  CrmDependencies,
  CrmInjectionToken,
  ObjectType,
  RecordChangeType,
} from '../../../../../../tokens-and-interfaces';
import { TranslateForCrmObjectService } from '../../../../../../i18n/translate-for-crm-object/translate-for-crm-object.service';
import { TranslationModule } from '../../../../../../i18n/translation-module';

import { CrmFieldService, StandardExternalIds, StandardIds } from '../../../../../../shared-services';
import { ActivityCardComponent } from '../activity-card/activity-card.component';
import { ActivityAssociationService, ActivityRecordChangeIcons } from '../../../../activities';
import { RouterLink } from '@angular/router';
import { CrmObjectService, CrmObjectDisplayService } from '../../../../../../shared-services';

@Component({
  selector: 'crm-activity-record-change-base',
  templateUrl: './record-change-base.component.html',
  styleUrls: ['./record-change.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    TranslationModule,
    MatCardModule,
    GalaxyPipesModule,
    GalaxyAvatarModule,
    ActivityCardComponent,
    RouterLink,
  ],
})
export class CrmActivityRecordChangeBaseComponent {
  activity = input<ActivityInterface>({});
  showPrimaryAssociation = input<boolean>(false);

  private readonly activity$ = toObservable(this.activity);
  private readonly showPrimaryAssociation$ = toObservable(this.showPrimaryAssociation);

  protected readonly association$ = this.activity$.pipe(
    map((activity) => {
      if (activity?.associations && activity.associations?.length > 0) {
        return activity.associations[0];
      }
      console.warn('No associations found for activityId: ', activity.crmObjectId);
      return undefined;
    }),
  );

  protected readonly changeLogData$ = combineLatest([this.association$, this.activity$, this.config.namespace$]).pipe(
    switchMap(([association, activity, namespace]) => {
      const changeId = this.getStringFieldData(activity, StandardIds.RecordChangeReferenceFieldID);
      const objectId = association?.crmObjectId;
      const objectType = association?.crmObjectType;
      if (!objectId || !changeId || !namespace || !objectType) {
        return of(null);
      }

      return this.changeLogService
        .getMultiChangeLog({
          crmObjectType: objectType,
          namespace: namespace,
          crmChangelogIdentifiers: [
            {
              crmObjectId: objectId,
              crmChangeId: changeId,
            },
          ],
        })
        .pipe(map((res) => res?.changeLogs?.[0] || null));
    }),
  );

  private readonly userServiceData$ = this.changeLogData$.pipe(
    switchMap((changeLogData) => {
      const createdByUserId = changeLogData?.createdByUserId || '';
      return createdByUserId
        ? this.config.services?.userService?.getMultiUsers([createdByUserId]) || of(null)
        : of(null);
    }),
  );

  private readonly detailsUpdatedTranslation$ = this.association$.pipe(
    switchMap((association) => {
      const objectType = association?.crmObjectType as ObjectType;
      return this.translateForCrmObjectService.getTranslationForCrmObject(
        objectType || 'Contact',
        'RECORD_CHANGE_ACTIVITY.DETAILS_UPDATED',
      );
    }),
  );

  private readonly recordChangeType$ = this.activity$.pipe(
    map(
      (activity) => this.getStringFieldData(activity, StandardExternalIds.ActivityRecordChangeType) as RecordChangeType,
    ),
  );

  protected readonly isImportantFieldUpdated$ = this.recordChangeType$.pipe(
    map((recordChangeType) => recordChangeType === 'IMPORTANT_FIELD_UPDATED'),
  );

  protected readonly icon$ = this.association$.pipe(
    map((association) => {
      const objectType = association?.crmObjectType;
      if (!objectType) return '';
      return ActivityRecordChangeIcons[objectType as string];
    }),
    startWith(''),
  );

  protected readonly changeLogUserName$ = this.userServiceData$.pipe(
    map(
      (userServiceData) =>
        userServiceData?.[0]?.displayName ?? this.translate.instant('ACTIVITY.RECORD_CHANGE.USER_UNKNOWN'),
    ),
  );

  protected readonly associatedObject$ = this.association$.pipe(
    switchMap((association) => {
      const objectId = association?.crmObjectId;
      const objectType = association?.crmObjectType;
      if (!objectId || !objectType) {
        return of(null);
      }
      return this.objectService.getMultiObject(objectType as ObjectType, [objectId]);
    }),
    map((response) => {
      if (!response?.crmObjects?.[0]) {
        return null;
      }
      return response.crmObjects[0];
    }),
  );

  protected readonly primaryAssociationDisplayName$ = combineLatest([this.associatedObject$, this.association$]).pipe(
    map(([object, association]) => {
      const objectType = association?.crmObjectType;
      if (!object || !objectType) {
        return '';
      }
      return this.objectDisplayService.getObjectDisplayName(objectType as ObjectType, object);
    }),
  );

  protected readonly primaryAssociationRoute$ = combineLatest([this.config.routePrefix$, this.association$]).pipe(
    map(([prefix, association]) => {
      const objectId = association?.crmObjectId;
      const objectType = association?.crmObjectType as ObjectType;
      const objectSubtype = association?.crmObjectSubtype;
      if (!prefix || !objectId || !objectType) {
        return '';
      }

      const linkUrl = this.activityAssociationService.constructProfilePageRoute(objectId, objectType, objectSubtype);
      return linkUrl ? `${prefix}/${linkUrl}` : '';
    }),
  );

  protected readonly headerText$ = combineLatest([
    this.recordChangeType$,
    this.association$,
    this.activity$,
    this.showPrimaryAssociation$,
    this.detailsUpdatedTranslation$,
  ]).pipe(
    map(([recordChangeType, association, activity, showPrimaryAssociation, detailsUpdatedTranslation]) => {
      const computedObjectType = association?.crmObjectType;
      const sourceName = activity ? this.getStringFieldData(activity, StandardIds.ActivitySourceName) : '';
      if (!computedObjectType) {
        return '';
      }

      if (recordChangeType === 'CREATED') {
        const translationKey = sourceName ? 'ACTIVITY.RECORD_CHANGE.CREATED_VIA' : 'ACTIVITY.RECORD_CHANGE.CREATED';
        let translation = this.translate.instant(translationKey);
        if (showPrimaryAssociation) {
          translation = translation.toLowerCase();
        }

        return sourceName ? `${translation} ${sourceName}` : translation;
      }

      if (recordChangeType === 'IMPORTANT_FIELD_UPDATED') {
        return detailsUpdatedTranslation;
      }

      return '';
    }),
  );

  protected readonly contentText$ = of('');

  constructor(
    private readonly fieldService: CrmFieldService,
    protected readonly translateForCrmObjectService: TranslateForCrmObjectService,
    private readonly changeLogService: CRMChangeLogApiService,
    private readonly objectService: CrmObjectService,
    private readonly objectDisplayService: CrmObjectDisplayService,
    protected readonly translate: TranslateService,
    private readonly activityAssociationService: ActivityAssociationService,
    @Inject(CrmInjectionToken) protected readonly config: CrmDependencies,
  ) {}

  protected getStringFieldData(activity: ActivityInterface, fieldId: string): string {
    return this.fieldService.getFieldValueFromCrmObject(activity, fieldId)?.stringValue ?? '';
  }
}
